import type { ReactNode } from 'react';
import { Badge, Button, Card, CardHeader, CardTitle } from '@paige/ui';
import type { Statement, StatementLine } from './pb-types';
import { day, dollars, monthWord, statementLook } from './pb-words';
import { FactList, ScrollTable, Th } from './pb-parts';
import './partner-billing.css';

export type StatementAction =
  { kind: 'paid' | 'void' } | { kind: 'adjust'; corrects: StatementLine | null };

export interface StatementDetailProps {
  statement: Statement;
  today: string;
  canWrite: boolean;
  csvHref: (number: string) => string;
  onShow: (statementId: string) => void;
  onAction: (a: StatementAction) => void;
}

const payable = (s: Statement): boolean => s.state === 'issued' && !s.paidOn;

/** One statement: its totals and dates, its lines and adjustments, and what staff may do with it now. */
export function StatementDetail(p: StatementDetailProps): ReactNode {
  const s = p.statement;
  const look = statementLook(s, p.today);
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {s.number
            ? `${s.number}, ${monthWord(s.month)}`
            : `Open statement, ${monthWord(s.month)}`}
        </CardTitle>
        <Badge tone={look.tone}>{look.word}</Badge>
      </CardHeader>
      <Totals s={s} />
      <Links s={s} onShow={p.onShow} />
      <Lines
        s={s}
        canWrite={p.canWrite}
        onCorrect={(l) => p.onAction({ kind: 'adjust', corrects: l })}
      />
      <StatementActions {...p} />
    </Card>
  );
}

function StatementActions(p: StatementDetailProps): ReactNode {
  const s = p.statement;
  const may = p.canWrite && payable(s);
  return (
    <div className="pb-actions">
      {s.number ? (
        <Button asChild variant="secondary">
          <a href={p.csvHref(s.number)} download>
            Download CSV<span className="pui-visually-hidden"> of {s.number}</span>
          </a>
        </Button>
      ) : null}
      {may ? <Button onClick={() => p.onAction({ kind: 'paid' })}>Mark paid…</Button> : null}
      {may ? (
        <Button variant="danger" onClick={() => p.onAction({ kind: 'void' })}>
          Void and reissue…
        </Button>
      ) : null}
      {p.canWrite ? (
        <Button variant="ghost" onClick={() => p.onAction({ kind: 'adjust', corrects: null })}>
          Add adjustment…
        </Button>
      ) : null}
    </div>
  );
}

function Totals({ s }: { s: Statement }): ReactNode {
  const facts = [
    {
      label: s.totalFinal ? 'Owed' : 'Owed so far',
      value: dollars(s.total),
      note: `list ${dollars(s.listTotal)}`,
    },
    { label: 'Issued', value: s.issuedAt ? day(s.issuedAt) : 'On the 1st' },
    { label: 'Due', value: day(s.dueOn) },
    { label: 'Paid', value: s.paidOn ? day(s.paidOn) : 'Not yet', note: s.paidRef },
    { label: 'QuickBooks customer', value: s.quickbooksCustomer ?? '—' },
  ];
  return <FactList facts={facts} />;
}

/** A void statement points at its reissue, a reissue at what it replaced. */
function Links({ s, onShow }: { s: Statement; onShow: (id: string) => void }): ReactNode {
  if (!s.supersededBy && !s.supersedes) return null;
  return (
    <p className="ad-small" style={{ marginTop: 'var(--paige-space-3)' }}>
      {s.state === 'void' ? `Void ${day(s.voidAt)}${s.voidWhy ? `: ${s.voidWhy}` : ''}. ` : null}
      {s.supersededBy ? (
        <button type="button" className="pb-link" onClick={() => onShow(s.supersededBy ?? '')}>
          Show its reissue
        </button>
      ) : null}
      {s.supersedes ? (
        <button type="button" className="pb-link" onClick={() => onShow(s.supersedes ?? '')}>
          Show the statement it replaced
        </button>
      ) : null}
    </p>
  );
}

function LinesHead({ correctable }: { correctable: boolean }): ReactNode {
  return (
    <>
      <Th>Company and line</Th>
      <Th>Service</Th>
      <Th num>List</Th>
      <Th num>Rate</Th>
      <Th num>Amount</Th>
      {correctable ? (
        <Th>
          <span className="pui-visually-hidden">Correct</span>
        </Th>
      ) : null}
    </>
  );
}

function Lines(p: {
  s: Statement;
  canWrite: boolean;
  onCorrect: (l: StatementLine) => void;
}): ReactNode {
  const correctable = p.canWrite && p.s.state === 'issued';
  if (p.s.lines.length === 0) return <p className="ad-small pui-muted">No lines yet.</p>;
  const foot = (
    <tr>
      <td colSpan={2}>{p.s.totalFinal ? 'Total' : 'Total so far'}</td>
      <td className="is-num">{dollars(p.s.listTotal)}</td>
      <td />
      <td className="is-num">{dollars(p.s.total)}</td>
      {correctable ? <td /> : null}
    </tr>
  );
  return (
    <div style={{ marginTop: 'var(--paige-space-4)' }}>
      <ScrollTable head={<LinesHead correctable={correctable} />} foot={foot} label="Lines">
        {p.s.lines.map((l) => (
          <LineRow key={l.id} l={l} correctable={correctable} onCorrect={p.onCorrect} />
        ))}
      </ScrollTable>
    </div>
  );
}

function LineRow(p: {
  l: StatementLine;
  correctable: boolean;
  onCorrect: (l: StatementLine) => void;
}) {
  const l = p.l;
  return (
    <tr className={l.kind === 'adjustment' ? 'is-adjustment' : undefined}>
      <td>
        {l.companyName ?? 'Account adjustment'}
        <span className="pb-table__sub">
          {l.description}
          {l.corrects ? ` (corrects line ${l.corrects})` : ''}
        </span>
      </td>
      <td>{l.serviceTo ? `${day(l.serviceFrom)} to ${day(l.serviceTo)}` : day(l.serviceFrom)}</td>
      <td className="is-num">{l.listAmount === null ? '—' : dollars(l.listAmount)}</td>
      <td className="is-num">{l.ratePercent === null ? '—' : `${l.ratePercent}% off`}</td>
      <td className="is-num">{dollars(l.amount)}</td>
      {p.correctable ? (
        <td>
          <button
            type="button"
            className="pb-link"
            aria-label={`Correct line ${l.id}`}
            onClick={() => p.onCorrect(l)}
          >
            Correct
          </button>
        </td>
      ) : null}
    </tr>
  );
}
